<template>
  <div
    class="product-card"
    :class="[
      $blockClass(blockClass, 'product-card'),
      { [$class('loading')]: !product },
    ]"
  >
    <div
      class="product-card__images"
      :class="[
        { 'product-card__images--has-secondary': secondaryImage },
        $blockClass(blockClass, 'product-card-images')
      ]"
    >
      <a
        :href="productUrl"
        role="button"
        ontouchstart=""
        class="product-card__image-wrap product-card__image-wrap--primary"
      >
        <responsive-image
          :alt="imageAlt"
          :class="$blockClass(blockClass, 'product-card-image')"
          :force-update="true"
          :image="primaryImage || {}"
          :ratio="imageRatio"
          :style="{ animationDelay: animationDelay }"
          :title="imageAlt"
          :lazyload="index > 3"
          :sizes="'(min-width: 1440px) 328px, (min-width: 768px) 23vw, 47vw'"
          :image-high-priority="index < 2"
          block-class="product-card"
          min-max="200-500"
        />
        <span
          class="visually-hidden"
          v-text="$string(
            'accessibility.image',
            { replace: { description: 'Product'}}
          )"
        />
      </a>

      <a
        v-if="secondaryImage && !isMobile"
        :href="productUrl"
        class="product-card__image-wrap product-card__image-wrap--secondary"
      >
        <responsive-image
          :alt="imageAlt"
          :class="$blockClass(blockClass, 'product-card-image')"
          :force-update="true"
          :image="secondaryImage || {}"
          :ratio="imageRatio"
          :title="imageAlt"
          block-class="product-card"
          min-max="200-500"
        />
        <span
          class="visually-hidden"
          v-text="$string(
            'accessibility.image',
            { replace: { description: 'Product'}}
          )"
        />
      </a>

      <badge
        v-if="badgeTag"
        block-class="product-card"
        :label="badgeTag.replace(/badge.*:/, '')"
        :modifiers="badgeModifiers"
      />

      <quick-add
        v-if="quickAdd && quickAddNamespace"
        :namespace="quickAddNamespace"
        :product="product"
        :wishlist="wishlist"
        :product-card-color="allColors && allColors[selectedColorIndex]"
        block-class="product-card"
      />
    </div>

    <div
      class="product-card__footer"
      :class="$blockClass(blockClass, 'product-card-footer')"
    >
      <div
        v-if="limitedColors?.length"
        class="product-card__variants"
        :class="$blockClass(blockClass, 'product-card-variants')"
      >
        <ul class="product-card__swatches">
          <li
            v-for="(color, swatchIndex) in limitedColors"
            :key="swatchIndex"
            class="product-card__swatch"
          >
            <color-swatch
              :product="product"
              :class="$blockClass(blockClass, 'product-card-color-swatch')"
              :color="color"
              :is-active="selectedColorIndex === swatchIndex"
              @mouseover="() => handleMouseOverSwatch(color)"
              @mouseleave="() => handleMouseLeaveSwatch()"
              @click.prevent="() => handleClickSwatch(color, swatchIndex)"
            />
          </li>
        </ul>
        <a
          class="product-card__additional-colors"
          :href="productUrl"
        >
          <span
            v-if="hiddenColorCount"
            class="caption"
            v-text="`+${hiddenColorCount} ${hiddenColorText}`"
          />
        </a>
      </div>

      <a
        :href="productUrl"
        class="product-card__details"
        :class="$blockClass(blockClass, 'product-card-details')"
      >
        <div>
          <p
            class="product-card__title body-2"
            :class="$blockClass(blockClass, 'product-card-title')"
            :style="{ animationDelay: animationDelay }"
            v-text="product.title"
          />
          <div
            v-if="product?.metafields?.subtitle?.message"
            class="product-card__eyebrow"
          >
            <eyebrow
              :enable-bullet="true"
              :text="product?.metafields?.subtitle?.message?.value"
              class="caption"
            />
          </div>
      </div>

        <template v-if="isMobile">
          <slot name="variantTitle" />
        </template>

        <product-prices
          :class="[
            $blockClass(blockClass, 'product-card-prices'),
            'body-2',
          ]"
          :available="product?.available"
          :compare-at-price="variantCompareAtPrice"
          :compare-at-price-highest="variantCompareAtPriceHighest"
          :price="variantPrice"
          :maxPrice="product?.price?.max"
          :reverse="true"
          :style="{ animationDelay: animationDelay }"
          block-class="product-card"
        />
      </a>

      <template v-if="!isMobile">
        <slot name="variantTitle" />
      </template>
    </div>

    <wishlist-button
      v-if="wishlist"
      :product-id="product.id"
      :variant-id="variantId"
      block-class="product-card"
      class="product-card__action"
      icon="misc.heartFilled"
    />
  </div>
</template>

<script>

/**
 * Vue: Product card
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Number} index - Product card's forloop index.
 * @param {String} namespace - A unique namespace to be used for quick add.
 * e.g. 'product-carousel-[sectionId]'
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {Boolean} quickAdd - Enable quick add
 * @param {Boolean} wishlist - Enable wishlist
 *
 */
import { mapActions, mapState } from 'vuex'

import Badge from '~global/badge/badge'
import ColorSwatch from '~global/color-swatch/color-swatch'
import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'

import { values as timings } from '~/config/timings'

export default {
  name: 'ProductCard',

  components: {
    Badge,
    ColorSwatch,
    ProductPrices,
    ResponsiveImage,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    index: {
      type: Number,
      default: 0,
    },
    namespace: {
      type: String,
      default: '',
    },
    preselectedColor: {
      type: String,
      default: '',
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    quickAdd: {
      type: Boolean,
      default: true,
    },
    wishlist: {
      type: Boolean,
      default: true,
    },
    collectionUrl: {
      type: String,
      default: '',
    },
  },

  emits: 'color-update',

  data() {
    return {
      desktopColorLimit: 4,
      imagesSortOrder: ['primary', 'secondary'],
      mobileColorLimit: 2,
      primaryImage: false,
      secondaryImage: false,
      selectedColorIndex: 0,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute variants containing active color.
     * @returns {Array|Boolean}
     */
    activeColorVariants() {
      return this.product?.variants?.filter((variant) => {
        const colorOption = variant.selectedOptions.find((option) =>
          option.name.toLowerCase() === 'color',
        )

        if (!colorOption) {
          return false
        }

        const sanitizedBoostValue = colorOption.value.split(' / ').map(
          option => option.trim(),
        )

        return sanitizedBoostValue.indexOf(this.currentColor) > -1 ||
          this.currentColor === colorOption.value
      })
    },

    /**
     * Compute all colors from product options.
     * @returns {Array}
     */
    allColors() {
      if (
        !Array.isArray(this.product.options) ||
        !this.product.options.map(opt => opt?.name?.toLowerCase())?.includes('color')
      ) {
        return null
      }

      const colors = this.product.variants.reduce((agg, item) => {
        if(item?.selectedOptions.length){
          agg.push(item.selectedOptions.find(opt =>opt.name?.toLowerCase() == 'color').value.split(' / ')[0])
        }
        return agg
      }, []).map(color => color.trim())

      /**
       * Move preselected color to the beginning of the swatch colors.
       */
      if (this.preselectedColor && colors) {
        const colorIndex = colors.map(color =>
          color.toLowerCase(),
        ).indexOf(this.preselectedColor.toLowerCase())

        if (colorIndex > -1) {
          const color = colors.splice(colorIndex, 1)
          colors.unshift(color[0])
        }
      }

      return [...new Set(colors)]
    },

    /**
     * Compute animation delay.
     * @returns {String}
     */
    animationDelay() {
      return `${this.index * timings.xs}s`
    },

    /**
     * Compute product card badge tag.
     * - Only one badge at a time. If multiple tags exist only the first tag
     * is displayed, and the tag badge: is prioritised over badge_pink:
     * @returns {String|Boolean}
     */
    badgeTag() {
      if (!this.product?.tags) {
        return false
      }

      const badgeTags = [
        'badge:',
        'badge_pink:',
      ]

      const tag = this.product.tags.find((productTags) =>
        badgeTags.find((tag) => productTags.indexOf(tag) > -1),
      )

      return tag || false
    },

    /**
     * Compute badge modifiers.
     * @returns {String|Boolean}
     */
    badgeModifiers() {
      if (!this.badgeTag) {
        return false
      }

      const matches = this.badgeTag.toLowerCase().match(/badge_(.+):/)

      return matches && matches[1]
    },

    /**
     * Compute current color name.
     * @returns {String}
     */
    currentColor() {
      return Array.isArray(this.allColors) && this.allColors[this.selectedColorIndex]
    },

    /**
     * Compute number of colors not shown in swatches.
     * @returns {Number|Boolean}
     */
    hiddenColorCount() {
      if (!Array.isArray(this.allColors)) {
        return false
      }

      return this.allColors.length - this.limitedColors.length
    },

    /**
     * Computed text depends on color length in swatch.
     * @returns {String}
     */
    hiddenColorText() {
      return this.allColors.length -
        this.limitedColors.length <= 1 ? 'Color' : 'Colors'
    },

    /**
     * Compute image alt text.
     * @returns {String}
     */
    imageAlt() {
      const currentColor = this.currentColor ? this.currentColor : ``

      return `Dormify ${this.product.title} ${currentColor}`
    },

    /**
     * Compute image ratio.
     * @returns {String}
     */
    imageRatio() {
      return this.isMobile ? '1:1' : '33:34'
    },

    /**
     * Compute limited selection of colors based on color limit.
     * @returns {Array}
     */
    limitedColors() {
      if (!Array.isArray(this.allColors)) {
        return []
      }

      const limit = this.isMobile ? this.mobileColorLimit : this.desktopColorLimit
      return this.allColors.slice(0, limit)
    },

    /**
     * Compute variants containing active color sorted by price (cheapest first)
     * @returns {Array}
     */
    sortedActiveColorVariants() {
      if (!this.activeColorVariants || !this.activeColorVariants.length) {
        return []
      }

      return [...this.activeColorVariants].sort(
        (a, b) => a.price.amount > b.price.amount,
      )
    },

    /**
     * Compute product card compare at price from current variant.
     * @returns {Number|Boolean}
     */
    variantCompareAtPrice() {
      if (this.product?.hasOnlyDefaultVariant) {
        return this.product?.compareAtPrice?.amount
      }

      if (
        !this.sortedActiveColorVariants ||
        !this.sortedActiveColorVariants.length
      ) {
        return this.product?.compareAtPrice?.amount
      }

      const cheapest = this.sortedActiveColorVariants[0].compareAtPrice?.amount

      return cheapest
    },

    /**
     * Compute product card compare at price from current variant.
     * @returns {Number|Boolean}
     */
     variantCompareAtPriceHighest() {
      if (this.product?.hasOnlyDefaultVariant) {
        return this.product?.compareAtPrice?.amount
      }

      if (
        !this.sortedActiveColorVariants ||
        !this.sortedActiveColorVariants.length
      ) {
        return this.product?.compareAtPrice?.amount
      }

      const highestVariantPrice = this.sortedActiveColorVariants[this.sortedActiveColorVariants.length-1].compareAtPrice?.amount

      return highestVariantPrice
    },

    variantId() {
      return this.sortedActiveColorVariants[0]?.id ||
        (this.product?.variants && this.product.variants[0]?.id)
    },

    /**
     * Compute product card price from current variant.
     * @returns {Number}
     */
    variantPrice() {
      const queryString = window.location.search
      const urlParams = new URLSearchParams(queryString)
      const sort = urlParams.get('sort')

      if(sort === 'price-descending'){
        return this.product?.price?.max
      }

      if (
        this.product?.hasOnlyDefaultVariant ||
        !this.product?.price?.varies ||
        !this.sortedActiveColorVariants ||
        !this.sortedActiveColorVariants.length
      ) {
        return this.product?.price?.amount
      }

      return this.sortedActiveColorVariants[0].price.amount
    },

    /**
     * Compute the unique quick add namespace.
     * @returns {string}
     */
    quickAddNamespace() {
      if (!this.product || this.quickAddNamespace || !this.namespace) {
        return ''
      }

      return `${this.namespace}-quick-add-${this.product.id}`
    },

    /**
     * Compute the collection url to get product url with collection alias.
     * @returns {String}
     */
    productUrl() {
      let url = '/products/' + this.product.handle
      if (this.collectionUrl) {
        url = this.collectionUrl + url
      }
      if (this.variantId) {
        url += '?variant=' + this.variantId
      }
      return url
    },
  },

  mounted() {
    this.setInitialImages()
  },

  updated() {
    this.setInitialImages()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      loadComponent: 'components/load',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Get images from a color name.
     * @param {String} color - Color name.
     */
    getImages(color) {
      if (!color) {
        return []
      }

      return this.product.images.filter((image) => {
        return image.alt?.toLowerCase() === color.toLowerCase()
      })
    },

    /**
     * Handle swatch click event.
     * - Updates selected color.
     * @param {String} color - Color name.
     * @param {Number} index - Swatch index.
     */
    handleClickSwatch(color, index) {
      this.selectedColorIndex = index
      this.updateImages(color)
      this.$emit('color-update', color)
    },

    /**
     * Handle mouseleave on swatch event.
     * - Reverts product card images back to selected color.
     */
    handleMouseLeaveSwatch() {
      this.updateImages(this.currentColor)
    },

    /**
     * Handle mouseover on swatch event.
     * - Updates product card images temporarily.
     * @param {String} color - Color name.
     */
    handleMouseOverSwatch(color) {
      this.updateImages(color)
    },

    /**
     * Set initial product card images.
     */
    setInitialImages() {
      if (this.primaryImage) {
        return
      }

      if (Array.isArray(this.allColors)) {
        this.updateImages(this.allColors[0])
      } else {
        this.updateImages(false)
      }
    },

    /**
     * Update product card images.
     * @param {String|Boolean} color - Color name. If false default to featured
     *  image.
     */
    updateImages(color) {
      if (!color) {
        this.primaryImage = this.product.featuredImage

        if (this.product?.images?.length > 1) {
          this.secondaryImage = this.product.images.slice(1).find(image => {
            const alt = image.alt?.toLowerCase() || ''
            return !alt.includes('visualizer') && !alt.includes('swatch')
          })
        }

        return
      }

      const images = this.getImages(color)

      this.primaryImage = images[0] || this.product.featuredImage
      this.secondaryImage = images[1]
    },
  },
}
</script>

<style lang="scss">
@import './product-card';
</style>
